import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import { TerraButton } from "@/components/Buttons";
import { ASSETS, IMG } from "@/constants/site";

const CHAPTERS = [
  {
    n: "Chapter 1",
    title: "Rooted in the Soil",
    art: ASSETS.leafSet1, // one leaf
    img: IMG.aboutSoil,
    bg: "bg-cream",
    text: "text-ink",
    label: "text-forest",
    glow: false,
    paras: [
      "Sita begins before the kitchen. It begins in the soil.",
      "We are intentional about where every ingredient comes from. We build real relationships with local farms and shop the markets while they are in season, choosing what is fresh, alive, and grown with care.",
      "When the markets close for the winter, we do not lower the bar. We partner with farm subscription boxes so the quality never slips, no matter the month.",
      "What we cook is only ever as good as where it started. So we start with the earth, and with people we trust.",
    ],
    testid: "about-chapter-1",
  },
  {
    n: "Chapter 2",
    title: "The Quiet Kitchen",
    art: ASSETS.leafSet2, // two leaves
    img: IMG.aboutKitchen,
    bg: "bg-cream",
    text: "text-ink",
    label: "text-terracotta",
    glow: false,
    paras: [
      "Then life shifted. After weight loss surgery and a lot of grief, I found my way into the kitchen and could not leave. Cooking became how I healed. It became a love language.",
      "I am Puerto Rican, Italian, and Indian, raised where three kitchens meet. Sita was shaped in that quiet, where food stopped being fuel and became how I said: sit down, eat, you are loved.",
    ],
    testid: "about-chapter-2",
  },
  {
    n: "Chapter 3",
    title: "Food That Holds You",
    art: ASSETS.leafSet3, // three leaves
    img: IMG.aboutFood,
    bg: "bg-deepforest",
    text: "text-cream",
    label: "text-gold",
    glow: true,
    paras: [
      "I cook food that is bold, healthy, and culturally rich, all at once. Nothing bland in the name of good for you. Nothing out of reach in the name of good food.",
      "Everything is made from scratch, and everything is made with intention. This is not performance nutrition. It is nourishment you can feel in your bones.",
      "Because I believe good food is a right, not a luxury. No matter your zip code, no matter your budget, you deserve a real meal.",
    ],
    testid: "about-chapter-3",
  },
  {
    n: "Chapter 4",
    title: "The Table Grows Longer",
    art: ASSETS.leafSet4, // four leaves
    img: IMG.aboutTable,
    bg: "bg-cream",
    text: "text-ink",
    label: "text-gold",
    glow: false,
    paras: [
      "What began as quiet meals for one turned into a table for many. That is Sita now.",
      "A monthly supper club in Jersey City, where strangers become friends over a menu that changes every time. Small-event catering for the dinners and celebrations you want to do right. Private events in our space, meal prep in your kitchen, a personal chef for the nights that matter.",
      "The table only grows longer. There is always another chair open, and another plate to share.",
    ],
    testid: "about-chapter-4",
  },
];

export default function About() {
  return (
    <div>
      <Seo
        title="About Sita — Our Essence, a Story of Growth"
        description="Sita is a return — to food that nourishes deeply, to quiet traditions, to meals made with memory in mind. Rooted in the ground, brought to life in the kitchen. Jersey City, NJ."
      />

      {/* ---------- Intro: our essence ---------- */}
      <section data-testid="about-intro" className="mx-auto max-w-3xl px-6 pb-16 pt-16 text-center md:pb-20 md:pt-24">
        <FadeUp>
          <img
            src={ASSETS.sitaLogo}
            alt="Sita — a hand-drawn circle of leaves around the wordmark sita"
            data-testid="about-logo"
            className="mx-auto h-32 w-32 md:h-40 md:w-40"
          />
          <Overline className="mt-10">Our essence</Overline>
          <h1 className="mt-4 font-display text-4xl font-light leading-snug text-forest md:text-5xl">
            Sita is a <span className="italic text-terracotta">return.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.12}>
          <p className="mt-8 leading-relaxed text-ink/85">
            To food that nourishes deeply, to quiet traditions, to meals made with memory in mind. Rooted in care and
            intuition, Sita was born from the belief that food is more than fuel. It is story, it is healing, it is
            connection. Seasonal selections of produce shape what we create, and the hands that prepare it do so with
            intention.
          </p>
          <p className="mt-5 leading-relaxed text-ink/85">
            Every meal begins in the heart and ends at the table with warmth, generosity, and love.
          </p>
        </FadeUp>
        <FadeUp delay={0.2}>
          <div className="mt-12 space-y-4 border-y border-forest/10 py-10">
            {[
              ["Where sourcing", " begins with care."],
              ["Where healing", " becomes flavor."],
              ["Where nourishment", " is felt deeply."],
              ["Where meals turn", " into connection."],
            ].map(([a, b]) => (
              <p key={a} className="font-display text-2xl font-light leading-snug text-forest md:text-3xl">
                {a} <span className="italic text-gold">{b}</span>
              </p>
            ))}
          </div>
          <p className="mt-10 font-display text-2xl font-light leading-snug text-forest md:text-3xl">
            Rooted in the ground, brought to life in the kitchen.
          </p>
          <p className="mt-4 font-display text-2xl font-light leading-snug text-ink/80 md:text-3xl">
            Sita is where <span className="italic text-terracotta">whole ingredients</span> meet{" "}
            <span className="italic text-terracotta">wholehearted cooking.</span>
          </p>
        </FadeUp>
      </section>

      {/* ---------- Chapters ---------- */}
      {CHAPTERS.map((c, i) => (
        <section key={c.n} data-testid={c.testid} className={`${c.bg} ${c.text}`}>
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-12">
              <FadeUp className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="flex flex-col items-start gap-8 lg:sticky lg:top-32">
                  <img
                    src={c.art}
                    alt=""
                    aria-hidden="true"
                    data-testid={`${c.testid}-leaves`}
                    className="h-24 w-auto max-w-full object-contain md:h-36"
                    style={c.glow ? { filter: "drop-shadow(0 0 26px rgba(248, 242, 234, 0.28))" } : undefined}
                  />
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-[0.24em] ${c.label}`}>{c.n}</p>
                    <h2 className={`mt-3 font-display text-4xl font-light leading-tight md:text-5xl ${i === 2 ? "text-cream" : "text-forest"}`}>
                      {c.title}
                    </h2>
                  </div>
                </div>
              </FadeUp>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:col-start-6" : "lg:col-start-6"}`}>
                <FadeUp delay={0.12}>
                  <div className={`space-y-6 leading-relaxed ${i === 2 ? "text-cream/85" : "text-ink/85"}`}>
                    {c.paras.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                  <div className="mt-10 overflow-hidden rounded-3xl">
                    <img src={c.img} alt={c.title} className="aspect-[16/10] w-full object-cover" />
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ---------- Closing ---------- */}
      <section data-testid="about-closing" className="bg-forest text-cream">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center md:py-28">
          <FadeUp>
            <span className="mx-auto block h-px w-14 bg-gold" aria-hidden="true" />
            <h2 className="mt-8 font-display text-4xl font-light md:text-5xl">There is always another chair open.</h2>
            <div className="mt-10">
              <TerraButton to="/supper-club" testid="about-come-to-table-btn">
                Come to the table <span aria-hidden="true">→</span>
              </TerraButton>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
